import { defineStore } from 'pinia';
import { Ref, computed, h, nextTick, reactive, ref, render, watch } from 'vue';

import { BREAKPOINTS, HazardsListWidth } from '@/constants';
import {
  DropdownSelected,
  HAZARD_MARKERS,
  HazardType,
  HazardView,
  MAPBOX_SATELLITE_STYLE,
  MAPBOX_STYLE,
  MapMode,
  MapboxImage,
  MapboxLabelLayer,
  MapboxLayer,
  MapboxSource,
  MapboxStyle,
  PIN_IMAGE,
  SelectedHazard,
  SelectedHazardViewType,
  TOTAL_FIRE_BAN_MARKER,
  overlaySections,
  updateMarkers,
  useHazardsStore,
} from '@/features/hazards';

import UiMapPopup from '@/features/hazards/presentation/components/UiMap/UiMapPopup/tooltip/UiMapPopup.vue';
import { bbox, booleanPointInPolygon, intersect, polygon } from '@turf/turf';
import { useBreakpoints, useWindowSize } from '@vueuse/core';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry, Point } from 'geojson';
import {
  AnyLayer,
  AnySourceData,
  FlyToOptions,
  GeoJSONSource,
  GeolocateControl,
  LngLatBoundsLike,
  LngLatLike,
  Map as MapboxMap,
  Marker,
  Popup,
  PopupOptions,
  ScaleControl,
} from 'mapbox-gl';

import { getSortedHazardFeatures } from '../helpers/get-sorted-hazard-features';

import { HeaderHeight, ToolbarHeight } from '@/common/helpers/sizes';
import { i18n } from '@/locales';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { isEmpty } from 'remeda';

const { locale } = i18n.global;
const breakpoints = useBreakpoints(BREAKPOINTS);
const isBreakpointMd = breakpoints.greater('md');
const isBreakpointXl = breakpoints.greater('xl');

export const useMapStore = defineStore('mapStore', () => {
  const hazardsStore = useHazardsStore();
  const mapbox = ref<MapboxMap | null>(null);
  const mapboxLanguage = ref<MapboxLanguage>(new MapboxLanguage());

  const geolocate = ref<GeolocateControl>();

  const mapElementSize = reactive({
    width: 0,
    height: 0,
  });

  const created = ref(false);
  const loaded = ref(false);

  const globalSources: Record<string, FeatureCollection<Geometry, GeoJsonProperties>> = {};

  // used for cluster caching
  const clusterMarkers: Ref<Record<string, Marker>> = ref({});
  const clusterMarkersOnScreen: Ref<Record<string, Marker>> = ref({});
  const clustersToClean: Ref<Set<string>> = ref(new Set());

  // visible unclustered markers
  const visibleHazards = ref<Feature<Geometry, GeoJsonProperties>[]>([]);

  // Selected overlays
  const overlayFilters = ref<DropdownSelected>(
    Object.fromEntries(
      overlaySections.map((section) => [section.label, Object.fromEntries(section.items.map((item) => [item.label, false]))])
    )
  );

  const isSomeOverlayFilterActive = computed<boolean>(() =>
    Object.values(overlayFilters.value).some((section) => Object.values(section).some((checked) => checked))
  );

  // List of Fire Ban LGAs
  const activeTotalFireBanLGAs = ref<string[]>([]);
  const lgaToIds = ref<Record<string, { warningId: string; fireBanItemId?: string }>>({});

  const selectedPopup = ref<Popup>();

  const selectedPopupHazard = ref<SelectedHazard | null>(null);
  const previousPopupHazardId = ref<string>(); // Track the previous popup hazard ID

  const panControlsExpanded = ref(false);

  const bearing = ref(0);

  const style = ref<MapboxStyle>(MapboxStyle.default);

  const osmEnabled = ref(false);

  const setMapbox = (newMap: MapboxMap | null) => {
    mapbox.value = newMap;
  };

  const toggleOverlay = (section: string, item: string) => {
    const layerId = overlaySections.find((_section) => _section.label === section)?.items.find((_item) => _item.label === item);
    overlayFilters.value[section][item] = !overlayFilters.value[section][item];
    if (layerId?.overlay) setLayerVisibility(layerId.overlay, overlayFilters.value[section][item]);
    if (layerId?.labelLayer) setLayerVisibility(layerId.labelLayer, overlayFilters.value[section][item]);
  };

  const setVisibleHazards = () => {
    if (!mapbox.value) return;

    const bounds = mapbox.value.getBounds();

    const viewportPolygon = polygon([
      [
        bounds.getNorthEast().toArray(),
        bounds.getNorthWest().toArray(),
        bounds.getSouthWest().toArray(),
        bounds.getSouthEast().toArray(),
        bounds.getNorthEast().toArray(),
      ],
    ]);

    const mapMode = hazardsStore.mapMode;

    let allMarkers: Feature<Geometry, GeoJsonProperties>[] = [];

    switch (mapMode) {
      case MapMode.Default:
        allMarkers = [
          ...(globalSources[MapboxSource.hazardsPolygonsSource]?.features ?? []),
          ...(globalSources[MapboxSource.hazardsFireShapePolygonsSource]?.features ?? []),
          ...(globalSources[MapboxSource.hazardsMarkersSource]?.features ?? []),
        ];
    }

    visibleHazards.value = allMarkers.filter((feature) => {
      if (feature.geometry.type === 'Point') {
        return booleanPointInPolygon(feature.geometry.coordinates, viewportPolygon);
      }
      if (feature.geometry.type === 'Polygon') {
        return intersect(feature.geometry, viewportPolygon);
      }
    });
  };

  const setSelectedPopupHazard = async () => {
    const hazard = selectedPopupHazard.value;
    if (!mapbox.value) {
      return;
    }

    if (!hazard) {
      // Close the popup if no hazard is selected
      closePopup();
      return;
    }

    if (hazard.fly) {
      const pointFeatures = hazardsStore.hazards?.[hazard.hazardType]?.find((h) => h.id === hazard.hazardId)?.pointFeatures;
      flyToFeatures(pointFeatures ?? [], 0);
    }

    const coordinates = _getPopupCoordinates(hazard);

    if (!coordinates) return;

    await createPopup({
      type: UiMapPopup,
      coordinates,
      popupElementProps: hazard,
    });
  };

  watch(selectedPopupHazard, setSelectedPopupHazard);

  const _getPopupCoordinates = (hazard: SelectedHazard): LngLatLike | undefined => {
    if (!mapbox.value) return;

    let allMarkers: Feature<Geometry, GeoJsonProperties>[] = [];
    switch (hazard.hazardType) {
      case HazardType.Warning:
      case HazardType.Incident:
      case HazardType.Centre:
      case HazardType.Closure:
        allMarkers = globalSources[MapboxSource.hazardsMarkersSource]?.features;
        break;
    }

    if (!allMarkers) return;

    const marker = allMarkers?.find((m) => {
      if (hazard.featureId) {
        return m.properties?.id === hazard.hazardId && m.properties?.featureId === hazard.featureId;
      }

      return m.properties?.id === hazard.hazardId;
    });

    if (!marker) return;

    const coordinates = (marker.geometry as Point).coordinates as LngLatLike;

    return coordinates;
  };

  const flyToMarker = (hazardId: string) => {
    if (!mapbox.value) return;

    const allMarkers = globalSources[MapboxSource.hazardsMarkersSource]?.features;

    if (!allMarkers) return;

    const selectedMarker = allMarkers.find((marker) => marker.properties?.id === hazardId);

    if (!selectedMarker) return;

    flyToPosition({
      center: (selectedMarker.geometry as Point).coordinates as LngLatLike,
      zoom: 13,
    });
  };

  const flyToFeatures = (features?: Feature<Geometry, GeoJsonProperties>[], duration?: number) => {
    if (isEmpty(features as [])) return;

    if (features!.length === 1 && features![0].geometry.type === 'Point') {
      flyToPosition({
        center: features![0].geometry.coordinates as LngLatLike,
        zoom: 13,
        duration: duration ?? 1000,
      });
      return;
    }

    const featureCollection: FeatureCollection<Geometry, GeoJsonProperties> = {
      type: 'FeatureCollection',
      features: features!,
    };

    setViewPort(bbox(featureCollection) as LngLatBoundsLike, duration, 200);
  };

  const setCreated = (newCreated: boolean) => {
    if (newCreated) {
      onCreated();
    }
    created.value = newCreated;
  };

  const setViewPort = (newViewPort: LngLatBoundsLike, duration: number = 1000, padding: number = 0) => {
    mapbox.value?.fitBounds(newViewPort, {
      duration: duration,
      padding: padding,
    });
  };

  const setBearing = (newBearing: number) => {
    bearing.value = newBearing;
  };

  const addOrSetSource = (id: string, source: AnySourceData) => {
    const existingSource = mapbox.value?.getSource(id) as GeoJSONSource;

    if (source.type === 'geojson' && source.data) globalSources[id] = source.data as FeatureCollection<Geometry, GeoJsonProperties>;

    if (!existingSource) {
      mapbox.value?.addSource(id, source);
      return;
    }

    if (source.type !== 'geojson') {
      return;
    }

    existingSource.setData(source.data as FeatureCollection<Geometry, GeoJsonProperties>);
  };

  const addOrSetSources = (sources: Record<string, AnySourceData>) => {
    Object.entries(sources).forEach(([id, source]) => {
      addOrSetSource(id, source);
    });
  };

  const addLayer = (layer: AnyLayer) => {
    if (!mapbox.value?.getLayer(layer.id)) mapbox.value?.addLayer(layer);
  };

  const addLayers = (layers: AnyLayer[]) => {
    layers.forEach((layer) => {
      addLayer(layer);
    });
  };

  const setStyle = (newValue: MapboxStyle) => {
    setOsmEnabled(false);

    if (newValue == MapboxStyle.satellite && style.value != MapboxStyle.satellite) {
      mapbox.value?.setStyle(MAPBOX_SATELLITE_STYLE);
    } else if (newValue == MapboxStyle.default && style.value != MapboxStyle.default) {
      mapbox.value?.setStyle(MAPBOX_STYLE);
    }

    style.value = newValue;
  };

  const setOsmEnabled = (newValue: boolean) => {
    if (newValue) {
      setLayerVisibility(MapboxLayer.osmLayer, true);
    } else {
      setLayerVisibility(MapboxLayer.osmLayer, false);
    }
    osmEnabled.value = newValue;
  };

  const setMapModeLayer = (mapMode: MapMode) => {
    switch (mapMode) {
      case MapMode.Default:
      default:
        setLayerVisibility(MapboxLayer.hazardsPolygonsLayer, true);
        setLayerVisibility(MapboxLayer.hazardsPolygonsStrokeLayer, true);
        setLayerVisibility(MapboxLayer.hazardsFireShapePolygonsLayer, true);
        setLayerVisibility(MapboxLayer.hazardsFireShapePolygonsStrokeLayer, true);
        setLayerVisibility(MapboxLayer.hazardsPolylinesStrokeLayer, true);
        break;
    }
  };

  const setLayerVisibility = (id: MapboxLayer | MapboxLabelLayer, visible: boolean) => {
    mapbox.value?.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
  };

  const addImage = async (id: string, src: string) => {
    if (!mapbox.value) return;

    const image = await new Promise((resolve: (value: HTMLImageElement) => void) => {
      const image = new Image(64, 64);

      /**
       * Fix an issue where the FDR and TFB icons fail with a:
       * `'CanvasRenderingContext2D': The canvas has been tainted by cross-origin data.` error.
       * @see - https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/crossOrigin, https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image
       *
       * Setting the crossOrigin property to 'anonymous' allows the image to be loaded without tainting the canvas.
       * */
      image.crossOrigin = 'anonymous';

      image.onload = () => resolve(image);

      image.src = src;
    });

    if (!mapbox.value || mapbox.value.hasImage(id) || !image) return;
    mapbox.value.addImage(id, image);
  };

  const addImages = async (images: MapboxImage[]) => {
    await Promise.all(images.map(async ({ id, url }) => await addImage(id, url)));
  };

  const initializeImages = async () => {
    /**
     * Should be called when the map is created
     */

    const images = [];
    // initialize images
    images.push(
      ...Object.entries(HAZARD_MARKERS).map(([key, value]) => ({
        id: key,
        url: value,
      }))
    );

    images.push(PIN_IMAGE);
    images.push(TOTAL_FIRE_BAN_MARKER);

    await addImages(images);
  };

  const initializeSources = () => {
    /**
     * Should be called when the map is loaded, but BEFORE initializeLayers
     */

    const sources: Record<string, AnySourceData> = {};

    // Boundaries and icons

    sources[MapboxSource.hazardsMarkersSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
      cluster: true,
      clusterRadius: 20,
      clusterMaxZoom: 20,
    };

    sources[MapboxSource.hazardsPolygonsSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    sources[MapboxSource.hazardsFireShapePolygonsSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    sources[MapboxSource.hazardsPolylinesSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    sources[MapboxSource.osmSource] = {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
    };

    sources[MapboxSource.addressPinSource] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    };

    addOrSetSources(sources);
  };

  const initializeLayers = () => {
    /**
     * Should be called when the map is loaded
     */

    const layers: AnyLayer[] = [];

    layers.push({
      id: MapboxLayer.osmLayer,
      source: MapboxSource.osmSource,
      type: 'raster',
      layout: {
        visibility: 'none',
      },
      paint: {
        'raster-fade-duration': 0,
      },
    });

    // Boundaries and icons

    layers.push({
      id: MapboxLayer.invisibleLayer,
      source: MapboxSource.hazardsMarkersSource,
      type: 'circle',
      paint: {
        'circle-opacity': 0,
        'circle-radius': 0,
      },
    });

    layers.push({
      id: MapboxLayer.addressPinLayer,
      source: MapboxSource.addressPinSource,
      type: 'symbol',
      layout: {
        'icon-image': 'pin',
        'icon-anchor': 'center',
        'icon-size': 0.6,
      },
    });

    layers.push({
      id: MapboxLayer.hazardsPolygonsLayer,
      source: MapboxSource.hazardsPolygonsSource,
      type: 'fill',
      paint: {
        'fill-color': ['case', ['has', 'fill'], ['get', 'fill'], '#231F20'],
        'fill-opacity': ['case', ['has', 'fill-opacity'], ['get', 'fill-opacity'], 0.5],
      },
      layout: {
        'fill-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    layers.push({
      id: MapboxLayer.hazardsPolygonsStrokeLayer,
      source: MapboxSource.hazardsPolygonsSource,
      type: 'line',
      paint: {
        'line-color': ['case', ['has', 'stroke'], ['get', 'stroke'], '#231F20'],
        'line-width': ['case', ['has', 'stroke-width'], ['get', 'stroke-width'], 1],
      },
      layout: {
        'line-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    layers.push({
      id: MapboxLayer.hazardsFireShapePolygonsLayer,
      source: MapboxSource.hazardsFireShapePolygonsSource,
      type: 'fill',
      paint: {
        'fill-color': ['case', ['has', 'fill'], ['get', 'fill'], '#231F20'],
        'fill-opacity': ['case', ['has', 'fill-opacity'], ['get', 'fill-opacity'], 0.5],
      },
      layout: {
        'fill-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    layers.push({
      id: MapboxLayer.hazardsFireShapePolygonsStrokeLayer,
      source: MapboxSource.hazardsFireShapePolygonsSource,
      type: 'line',
      paint: {
        'line-color': ['case', ['has', 'stroke'], ['get', 'stroke'], '#000000'],
        'line-width': ['case', ['has', 'stroke-width'], ['get', 'stroke-width'], 1],
      },
      layout: {
        'line-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    layers.push({
      id: MapboxLayer.hazardsPolylinesStrokeLayer,
      source: MapboxSource.hazardsPolylinesSource,
      type: 'line',
      paint: {
        'line-color': ['case', ['has', 'stroke'], ['get', 'stroke'], '#231F20'],
        'line-width': ['case', ['has', 'stroke-width'], ['get', 'stroke-width'], 1],
      },
      layout: {
        'line-sort-key': ['case', ['has', 'priority'], ['get', 'priority'], 0],
      },
    });

    addLayers(layers);
  };

  const initializeLanguage = () => {
    /**
     * Should be called when the map is loaded
     */

    const languageCode = locale.value.split('-')[0];
    setLanguage(languageCode);
  };

  const createPopup = async ({
    type = UiMapPopup,
    coordinates,
    popupElementProps,
    options,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    popupElementProps?: any;
    coordinates: LngLatLike;
    options?: PopupOptions;
  }) => {
    if (!mapbox.value) return;

    // Close the currently open popup (this triggers the 'close' event below)
    selectedPopup.value?.remove();

    let popup: Popup | undefined;

    await nextTick(() => {
      popup = new Popup({ anchor: 'bottom', ...(options ?? {}) }).setLngLat(coordinates).setHTML('<div id="map-popup-content"></div>');
      selectedPopup.value = popup;
      popup.addTo(mapbox.value!);

      const activePopupHazardId = selectedPopupHazard.value?.hazardId;
      // The same hazard can have multiple GeoJSON features selected independently (via featureId)
      const activePopupHazardFeatureId = selectedPopupHazard.value?.featureId;

      popup.once('close', () => {
        if (
          activePopupHazardId === selectedPopupHazard.value?.hazardId &&
          activePopupHazardFeatureId === selectedPopupHazard.value?.featureId
        ) {
          selectedPopupHazard.value = null;
          hazardsStore.toggleSelectHazard(null);
          previousPopupHazardId.value = activePopupHazardId;
          setTimeout(() => {
            previousPopupHazardId.value = undefined;
          }, 100);
        }
        // clearing this on a delay allows the same poly to be clicked to close the popup
        // after this delay, it would trigger an open instead
      });
    });

    await nextTick(() => {
      const popupComponent = h(type, { ...popupElementProps, coordinates });

      const element = document.getElementById('map-popup-content');

      if (!element) {
        throw new Error('Popup element not found');
      }

      render(popupComponent, element);

      const boundingRect = element.getBoundingClientRect();

      // At this point in the code, the popup may have rendered outside the map view.
      // First, we must check this, and then if it has, we must correct this by panning
      const margin = 16; // Distance we want between the top of the popup and the map border
      const popupPadding = 16; // Padding of the popup

      // The minimum y-position we want the top of the popup to be at
      const yThreshold = HeaderHeight.value + ToolbarHeight.value + margin;

      let hazardsListWidth = 0;
      if (hazardsStore.activeView === HazardView.Both) {
        if (isBreakpointXl.value) {
          hazardsListWidth = HazardsListWidth.Xl;
        } else if (isBreakpointMd.value) {
          hazardsListWidth = HazardsListWidth.Md;
        }
      }

      const xThreshold = hazardsListWidth + popupPadding + margin;

      if (boundingRect.x <= xThreshold || boundingRect.y <= yThreshold) {
        // If the y (top-left corner) has gone under the toolbar + margin, we must pan the map
        // so that the y is at this minimum y-position
        // This is the distance between the top-left corner and the y threshold
        // (e.g top corner is at y = 16), difference is 16 - 152 = -136
        // In that example, we would move -152 units down (152 units up)
        // The maximum value this can be is 0 (Since boundingRect.y <= yThreshold)

        mapbox.value?.panBy([Math.min(0, boundingRect.x - xThreshold), Math.min(0, boundingRect.y - yThreshold)], {
          duration: 100,
          easing: (time) => time,
        });
      }

      const { width } = useWindowSize();

      if (boundingRect.right > width.value) {
        mapbox.value?.panBy([boundingRect.right + margin - width.value, Math.min(0, boundingRect.y - yThreshold)], {
          duration: 100,
          easing: (time) => time,
        });
      }
    });

    return popup;
  };

  /**
   * Closes the currently open popup.
   */
  const closePopup = () => {
    selectedPopup.value?.remove();
  };

  const currentUpdate = ref<Promise<void>>(Promise.resolve());

  const safeUpdateMarkers = async () => {
    currentUpdate.value = currentUpdate.value.then(updateMarkers);
    await currentUpdate.value;
  };

  const updateMarkersAndVisibleHazards = () => {
    if (hazardsStore.activeView !== HazardView.List) {
      setVisibleHazards();
    }

    safeUpdateMarkers();
  };

  const onCreated = async () => {
    if (!mapbox.value) return;

    mapbox.value.setProjection('mercator');

    mapbox.value.on('rotate', () => {
      setBearing(mapbox.value?.getBearing() ?? 0);
    });

    mapbox.value.on('idle', updateMarkersAndVisibleHazards);
    mapbox.value.on('moveend', updateMarkersAndVisibleHazards);

    mapbox.value.on('data', (event) => {
      if (event.sourceId === MapboxSource.hazardsMarkersSource && event.isSourceLoaded && hazardsStore.activeView !== HazardView.List) {
        setVisibleHazards();
      }
    });

    mapbox.value.on('click', (e) => {
      const target = e.originalEvent.target as HTMLElement;
      if (target.classList.contains('mapboxgl-marker') || target.classList.contains('marker')) return;

      closePopup();
    });

    mapbox.value.on('mouseenter', [MapboxLayer.hazardsPolygonsLayer, MapboxLayer.hazardsFireShapePolygonsLayer], () => {
      if (!mapbox.value) return;
      mapbox.value.getCanvas().style.cursor = 'pointer';
    });

    mapbox.value.on('mouseleave', [MapboxLayer.hazardsPolygonsLayer, MapboxLayer.hazardsFireShapePolygonsLayer], () => {
      if (!mapbox.value) return;
      mapbox.value.getCanvas().style.cursor = '';
    });

    mapbox.value.on('click', [MapboxLayer.hazardsPolygonsLayer, MapboxLayer.hazardsFireShapePolygonsLayer], async (e) => {
      if (!mapbox.value || selectedPopupHazard.value || previousPopupHazardId.value) {
        return;
      }

      const sortedHazards = getSortedHazardFeatures(e.features!);
      const featureToSelect = sortedHazards.at(0);

      if (!featureToSelect) return;

      const {
        id: hazardId,
        entityType: hazardType,
        eventId,
      } = featureToSelect.properties as { eventId: string; id: string; entityType: HazardType };

      hazardsStore.toggleSelectHazard({ hazardId, eventId, hazardType, viewType: SelectedHazardViewType.Popup });
    });

    mapbox.value.addControl(new ScaleControl(), 'bottom-right');

    geolocate.value = new GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
    });

    mapbox.value.addControl(geolocate.value);

    mapbox.value?.setStyle(MAPBOX_STYLE);
  };

  const setLoaded = (newLoaded: boolean) => {
    if (newLoaded) {
      onLoaded();
    }
    loaded.value = newLoaded;
  };

  const onLoaded = () => {
    initializeLanguage();

    initializeImages();

    initializeSources();
    initializeLayers();
  };

  const teardown = () => {
    setCreated(false);
    setLoaded(false);

    setBearing(0);

    if (mapbox.value) {
      mapbox.value.remove();
      setMapbox(null);
    }
  };

  const flyToPosition = (options: FlyToOptions) => {
    mapbox.value?.flyTo({ maxDuration: 1000, ...options });
  };

  const flyToCurrentPosition = () => {
    geolocate.value?.trigger();
  };

  const setLanguage = (language: string) => {
    if (!mapbox.value) return;

    const currentStyle = mapbox.value.getStyle();
    const updatedStyle = mapboxLanguage.value.setLanguage(currentStyle, language);
    mapbox.value.setStyle(updatedStyle);
  };

  return {
    mapbox,
    setMapbox,
    created,
    setCreated,
    loaded,
    overlayFilters,
    isSomeOverlayFilterActive,
    toggleOverlay,
    setLoaded,
    setViewPort,
    bearing,
    style,
    activeTotalFireBanLGAs,
    lgaToIds,
    osmEnabled,
    setStyle,
    addImage,
    addImages,
    teardown,
    flyToPosition,
    flyToCurrentPosition,
    flyToFeatures,
    addLayer,
    addLayers,
    addOrSetSource,
    addOrSetSources,
    visibleHazards,
    setVisibleHazards,
    selectedPopupHazard,
    createPopup,
    clusterMarkers,
    clusterMarkersOnScreen,
    clustersToClean,
    setOsmEnabled,
    initializeLayers,
    initializeSources,
    flyToMarker,
    setMapModeLayer,
    geolocate,
    closePopup,
    mapElementSize,
    panControlsExpanded,
    setLanguage,
  };
});
